/**
 * ProductSection Component
 *
 * Main product display section on the homepage. Renders a 3-up grid
 * of ProductCards (Essential, Dynamic, Triple). Each card is now
 * self-contained in its own module (./ProductCard) so it can also be
 * used on the per-bundle landing pages without duplication.
 */

import React, { useState, useEffect } from 'react';
import { ProductCard, BOTTLE_IMAGES } from './ProductCard';
import { getPreloadedData, subscribeToPreloadUpdates } from '../../lib/preloadService';

// Background-load tub images so flavour switches feel instant.
const ImagePreloader: React.FC = () => {
  useEffect(() => {
    setTimeout(() => {
      Object.values(BOTTLE_IMAGES).forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }, 200);
  }, []);

  return null;
};

export const ProductSection: React.FC = () => {
  const [, setIsLoading] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [error] = useState<string | null>(null);

  useEffect(() => {
    const preloadedData = getPreloadedData();

    if (preloadedData.products.length > 0) {
      setProducts(preloadedData.products);
    }

    if (preloadedData.isReady) {
      setIsLoading(false);
    }

    const unsubscribe = subscribeToPreloadUpdates(() => {
      const updatedData = getPreloadedData();
      setProducts(updatedData.products);
      if (updatedData.isReady) {
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (error) {
    return (
      <div id="product-section" className="relative pb-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-16">
            <div className="glass-panel p-8 rounded-2xl bg-red-900/20 border border-red-500/50">
              <p className="text-xl text-red-200">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div id="product-section" className="relative pb-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-16">
            <div className="glass-panel p-8 rounded-2xl">
              <p className="text-xl text-white">No products available at this time.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section id="product-section" className="relative pb-16 bg-black" aria-labelledby="products-heading">
      <ImagePreloader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <h2 id="products-heading" className="text-4xl md:text-5xl font-bold mb-6 relative">
            <span className="gradient-text relative z-10">Premium Hydration & Vitamin Blends</span>
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-[#ff00ff]/0 via-[#ff00ff]/50 to-[#ff00ff]/0"></div>
          </h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product) => (
            <div key={product.id} className={product.id === 'triple-threat' ? 'md:col-span-2' : ''}>
              <ProductCard
                id={product.id}
                name={product.name}
                description={product.description}
                price={product.unitPrice}
                imagePlaceholder={product.images[0] || '/placeholder.png'}
                gradientFrom={product.gradients?.from || 'purple'}
                gradientTo={product.gradients?.to || 'fuchsia'}
                buttonFrom="purple"
                buttonTo="fuchsia"
                tier={product.metadata?.tier || 'Basic'}
                freeShipping={product.metadata?.freeShipping || false}
                shippingCost={product.metadata?.shippingCost || 995}
                productFlavor={product.metadata?.flavor || 'Lime Squash'}
                availableFlavors={product.metadata?.availableFlavors || ['Lime Squash', 'Orange Crush', 'Pineapple Punch']}
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 py-4 px-6 rounded-2xl bg-gradient-to-r from-fuchsia-900/20 to-cyan-900/20 border border-fuchsia-500/20">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-fuchsia-400 shrink-0">
            <rect width="18" height="12" x="3" y="8" rx="2" />
            <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
          </svg>
          <span className="text-white font-semibold text-base">Free shipping Australia-wide</span>
          <span className="text-gray-500 hidden sm:inline">·</span>
          <span className="text-gray-400 text-sm">Delivered in 2–3 business days</span>
        </div>
      </div>
    </section>
  );
};
